import { useMemo } from 'react'

import { ChartBar } from '@gravity-ui/icons'
import * as Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { useTranslation } from 'react-i18next'

import { EmptyState } from '@/components/ui/EmptyState'
import { useThemeStore } from '@/store/theme.store'
import { useVehicleAnalyticsStore } from '@/store/vehicleAnalytics.store'

import { CATEGORY_COLORS, chartTheme } from './chartTheme'

export function AdvanceByTypeChart() {
  const { t } = useTranslation()
  const entries = useVehicleAnalyticsStore(s => s.entries)
  const isDark = useThemeStore(s => s.dark)

  const advance = useMemo(() => entries.filter(e => e.category === 'advance'), [entries])

  const options = useMemo<Highcharts.Options>(() => {
    const typeTotals = new Map<string, number>()
    advance.forEach(entry => {
      typeTotals.set(entry.label, (typeTotals.get(entry.label) ?? 0) + entry.amountPLN)
    })

    const sorted = Array.from(typeTotals.entries()).sort((a, b) => b[1] - a[1])
    const { textColor, lineColor, gridLineColor, base } = chartTheme(isDark)

    return {
      ...base,
      chart: { ...base.chart, type: 'bar' },
      xAxis: {
        categories: sorted.map(([type]) => type),
        labels: { style: { color: textColor } },
        lineColor,
        tickColor: lineColor,
      },
      yAxis: {
        title: { text: 'PLN', style: { color: textColor } },
        labels: { style: { color: textColor } },
        gridLineColor,
      },
      legend: { enabled: false },
      tooltip: { valueDecimals: 2, valueSuffix: ' PLN' },
      plotOptions: {
        bar: {
          borderRadius: 4,
          borderWidth: 0,
          color: CATEGORY_COLORS.advance,
          dataLabels: {
            enabled: true,
            format: '{point.y:.2f} PLN',
            style: { color: textColor, textOutline: 'none', fontWeight: 'normal' },
          },
        },
      },
      series: [{
        type: 'bar',
        name: t('vehicle_analytics.categories.advance'),
        data: sorted.map(([, amount]) => Number(amount.toFixed(2))),
      }],
    }
  }, [advance, isDark, t])

  if (advance.length === 0) {
    return (
      <div className="flex h-full min-h-[300px] items-center justify-center">
        <EmptyState
          icon={<ChartBar className="size-8" />}
          title={t('analytics.no_expenses_period')}
          description={''}
        />
      </div>
    )
  }

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      containerProps={{ style: { height: '100%', minHeight: '320px' } }}
    />
  )
}
